import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useReactToPrint } from 'react-to-print';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { 
  Download, Printer, Home, CheckCircle, Loader2, GraduationCap, 
  School, MapPin, Calendar, User as UserIcon
} from 'lucide-react';
import { studentDashboardApi } from '../../services/studentDashboardApi';
import { formFillApi } from '../../services/formFillApi';
import logo from '../../img/logo3.png'; 

const ApplicationConfirmation = () => {
  const { appId } = useParams();
  const navigate = useNavigate();
  const componentRef = useRef();
  
  const [application, setApplication] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);

  useEffect(() => {
    const fetchApp = async () => {
      try {
        const res = await studentDashboardApi.getApplicationById(appId);
        if (res.success) {
            setApplication(res.application);
        }
      } catch (err) {
        console.error("Error fetching application:", err);
      } finally {
        setLoading(false);
      }
    };
    if (appId) fetchApp();
  }, [appId]);

  // --- PRINT FUNCTION ---
  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    documentTitle: `Vidyazon_Application_${appId}`,
  });

  // --- PDF DOWNLOAD FUNCTION ---
  const handleDownloadPDF = async () => {
    const element = componentRef.current;
    if (!element) return;

    try {
      setIsGeneratingPDF(true);
      
      // Capture the component as a high-resolution canvas
      const canvas = await html2canvas(element, {
        scale: 2, // Improves text sharpness
        useCORS: true, // Allows loading external images (profile pics/logos)
        logging: false,
        backgroundColor: "#ffffff",
      });

      const imgData = canvas.toDataURL('image/png');
      
      // Initialize PDF (A4 format)
      const pdf = new jsPDF({
        orientation: 'p',
        unit: 'mm',
        format: 'a4',
      });

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

      // Add image to PDF
      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
      
      // Trigger browser download
      pdf.save(`Vidyazon_Application_${appId}.pdf`);
    } catch (error) {
      console.error("PDF Generation Error:", error);
      alert("Failed to generate PDF. Please try using the Print option.");
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center">
        <Loader2 className="animate-spin w-10 h-10 text-blue-600"/>
      </div>
    );
  }

  if (!application) {
    return (
      <div className="h-screen flex items-center justify-center text-red-500 font-bold">
        Application not found
      </div>
    );
  }

  const { personalDetails, contactDetails, academicDetails, documents = {}, paymentStatus, paymentId, createdAt } = application;

  // --- LOGIC: Applied Institutions List ---
  let displayList = [];
  if (application.targetColleges && application.targetColleges.length > 0) {
    displayList = application.targetColleges.map((target) => {
      const details = application.collegeDetails?.find(
        (c) => c._id === target.collegeId || c.id === target.collegeId
      ) || {};
      return {
        name: details.collegeName || "Institution Name Unavailable",
        city: details.city,
        state: details.state,
        rating: details.rating,
        course: target.course || application.courseName || "General Admission"
      };
    });
  } else if (application.collegeDetails && application.collegeDetails.length > 0) {
    displayList = application.collegeDetails.map(c => ({
        name: c.collegeName, city: c.city, state: c.state, rating: c.rating, course: application.courseName
    }));
  } else if (application.collegeName) {
    displayList = [{
        name: application.collegeName, city: application.city || '-', state: application.state || '-', course: application.courseName
    }];
  }

  const displayName = personalDetails?.fullName || application.studentName || "Applicant Name";

  return (
    <div className="min-h-screen bg-gray-100 py-10 px-4 pt-24">
      {/* Top Action Bar */}
      <div className="max-w-5xl mx-auto mb-6 flex flex-col md:flex-row justify-between items-center gap-4 no-print print:hidden">
        <div className="flex items-center gap-2 text-green-700 bg-green-100 px-4 py-2 rounded-lg border border-green-200">
          <CheckCircle className="w-5 h-5" />
          <span className="font-semibold">Application Submitted Successfully!</span>
        </div>
        <div className="flex gap-3">
          <button 
            onClick={() => navigate('/student/dashboard')} 
            className="flex items-center gap-2 px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-800 transition shadow-sm"
          >
            <Home className="w-4 h-4" /> Dashboard
          </button>
          <button 
            onClick={handlePrint} 
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition shadow-sm"
          >
            <Printer className="w-4 h-4" /> Print Form
          </button>
          <button 
            onClick={handleDownloadPDF} 
            disabled={isGeneratingPDF}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition shadow-sm disabled:opacity-50"
          >
            {isGeneratingPDF ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            {isGeneratingPDF ? "Generating..." : "Download PDF"}
          </button>
        </div>
      </div>

      {/* Main Printable Area */}
      <div className="max-w-5xl mx-auto bg-white shadow-2xl print:shadow-none print:w-full print:max-w-none">
        <div ref={componentRef} className="p-8 md:p-12 relative overflow-hidden bg-white">
          
          {/* Watermark Logo */}
          <div className="absolute inset-0 flex items-center justify-center opacity-[0.03] pointer-events-none">
            <img src={logo} alt="" className="w-96 grayscale" />
          </div>

          {/* 1. Header */}
          <div className="border-b-4 border-blue-900 pb-6 mb-8 flex flex-col md:flex-row justify-between items-center relative z-10">
            <div className="flex items-center gap-4 mb-4 md:mb-0">
              <img src={logo} alt="Vidyazon Logo" className="h-16 w-auto object-contain" />
              <div className="border-l-2 border-gray-300 pl-4">
                <h1 className="text-3xl font-extrabold text-blue-900 tracking-tight">VIDYAZON</h1>
                <p className="text-sm font-semibold text-gray-600 tracking-wide uppercase">Centralized Admission Portal</p>
              </div>
            </div>
            <div className="text-right">
              <div className="bg-blue-50 px-4 py-2 rounded-lg border border-blue-100">
                <p className="text-xs font-bold text-gray-500 uppercase mb-1">Application Number</p>
                <p className="text-xl font-mono font-bold text-blue-900 tracking-wider">
                  {application.id ? application.id.substring(application.id.length - 10).toUpperCase() : 'N/A'}
                </p>
              </div>
              <p className="text-xs text-gray-500 mt-2">Generated on: {new Date().toLocaleDateString()}</p>
            </div>
          </div>

          {/* 2. Title */}
          <div className="text-center mb-8 relative z-10">
            <h2 className="text-2xl font-bold text-gray-800 uppercase decoration-blue-500 decoration-2 underline-offset-4">Application Receipt</h2>
            <p className="text-sm text-gray-500 mt-1">Academic Session 2025-2026</p>
          </div>

          {/* 3. Personal & Contact Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8 relative z-10">
            <div className="border border-gray-300 rounded-lg overflow-hidden h-fit">
              <div className="bg-gray-100 px-4 py-2 border-b border-gray-300 flex items-center gap-2">
                <UserIcon className="w-4 h-4 text-gray-600" />
                <span className="font-bold text-gray-700 text-sm uppercase">Candidate Personal Details</span>
              </div>
              <div className="text-sm divide-y divide-gray-200">
                <DetailRow label="Full Name" value={displayName} />
                <DetailRow label="Father's Name" value={personalDetails?.fatherName} />
                <DetailRow label="Mother's Name" value={personalDetails?.motherName} />
                <DetailRow label="Date of Birth" value={personalDetails?.dateOfBirth ? new Date(personalDetails.dateOfBirth).toLocaleDateString() : '-'} />
                <DetailRow label="Gender" value={personalDetails?.gender} />
                <DetailRow label="Category" value={personalDetails?.category} />
                <DetailRow label="Aadhar" value={personalDetails?.aadharNumber} />
              </div>
            </div>

            <div className="space-y-6">
                <div className="border border-gray-300 rounded-lg overflow-hidden">
                    <div className="bg-gray-100 px-4 py-2 border-b border-gray-300 flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-gray-600" />
                      <span className="font-bold text-gray-700 text-sm uppercase">Contact Information</span>
                    </div>
                    <div className="p-4 text-sm space-y-2">
                      <div className="flex flex-col mb-2">
                          <span className="font-semibold text-gray-600 text-xs uppercase">Address</span>
                          <span className="text-gray-900">
                          {contactDetails?.permanentAddress?.addressLine1}, {contactDetails?.permanentAddress?.city}, {contactDetails?.permanentAddress?.state} - {contactDetails?.permanentAddress?.pinCode}
                          </span>
                      </div>
                      <div className="flex border-t border-gray-200 pt-2">
                          <span className="w-24 font-semibold text-gray-600">Email:</span>
                          <span className="flex-1 text-gray-900 break-all">{contactDetails?.email}</span>
                      </div>
                      <div className="flex border-t border-gray-200 pt-2">
                          <span className="w-24 font-semibold text-gray-600">Mobile:</span>
                          <span className="flex-1 text-gray-900">{contactDetails?.mobile}</span>
                      </div>
                    </div>
                </div>

                {/* Photos/Signatures Section */}
                <div className="border border-gray-300 rounded-lg p-4 flex justify-around items-center bg-gray-50">
                    <div className="text-center">
                        <div className="w-24 h-28 bg-white border-2 border-gray-300 shadow-sm flex items-center justify-center overflow-hidden mb-1 mx-auto">
                            {documents?.profilePhoto ? (
                            <img src={documents.profilePhoto} alt="User" className="w-full h-full object-cover" />
                            ) : <span className="text-xs">No Photo</span>}
                        </div>
                        <span className="text-[10px] font-bold text-gray-500 uppercase">Photograph</span>
                    </div>
                    <div className="text-center">
                        <div className="w-32 h-12 bg-white border-2 border-gray-300 shadow-sm flex items-center justify-center overflow-hidden mb-1 mx-auto">
                             {documents?.signature ? (
                            <img src={documents.signature} alt="Sign" className="w-full h-full object-contain" />
                            ) : <span className="text-xs">No Sign</span>}
                        </div>
                        <span className="text-[10px] font-bold text-gray-500 uppercase">Signature</span>
                    </div>
                </div>
            </div>
          </div>

          {/* 4. Academic Section */}
          <div className="mb-8 border border-gray-300 rounded-lg overflow-hidden relative z-10">
            <div className="bg-gray-100 px-4 py-2 border-b border-gray-300 flex items-center gap-2">
                <GraduationCap className="w-4 h-4 text-gray-600" />
                <span className="font-bold text-gray-700 text-sm uppercase">Academic Qualifications</span>
            </div>
            <div className="overflow-x-auto">
                <table className="w-full text-xs md:text-sm text-left">
                    <thead className="bg-gray-50 text-gray-600 border-b border-gray-200">
                        <tr>
                            <th className="p-2 border-r">Class</th>
                            <th className="p-2 border-r">Board</th>
                            <th className="p-2 border-r">School</th>
                            <th className="p-2 border-r">Year</th>
                            <th className="p-2">Results</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                        <tr>
                            <td className="p-2 font-semibold border-r">10th</td>
                            <td className="p-2 border-r">{academicDetails?.class10?.boardUniversity || '-'}</td>
                            <td className="p-2 border-r">{academicDetails?.class10?.schoolCollege || '-'}</td>
                            <td className="p-2 border-r">{academicDetails?.class10?.passingYear || '-'}</td>
                            <td className="p-2">{academicDetails?.class10?.percentage || academicDetails?.class10?.cgpa || '-'}%</td>
                        </tr>
                        <tr>
                            <td className="p-2 font-semibold border-r">12th</td>
                            <td className="p-2 border-r">{academicDetails?.class12?.boardUniversity || '-'}</td>
                            <td className="p-2 border-r">{academicDetails?.class12?.schoolCollege || '-'}</td>
                            <td className="p-2 border-r">{academicDetails?.class12?.passingYear || '-'}</td>
                            <td className="p-2">{academicDetails?.class12?.percentage || academicDetails?.class12?.cgpa || '-'}%</td>
                        </tr>
                    </tbody>
                </table>
            </div>
          </div>

          {/* 5. Applied Institutions Section */}
          <div className="mb-8 border border-gray-300 rounded-lg overflow-hidden relative z-10">
            <div className="bg-blue-900 text-white px-4 py-3 border-b border-gray-300 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <School className="w-5 h-5" />
                <span className="font-bold text-sm uppercase">Applied Institutions</span>
              </div>
              <span className="text-xs font-semibold bg-white/20 px-3 py-1 rounded-full">
                Total: {displayList.length}
              </span>
            </div>
            
            <div className="overflow-x-auto">
                {displayList.length > 0 ? (
                    <table className="w-full text-xs md:text-sm text-left">
                        <thead className="bg-gray-50 text-gray-600 border-b border-gray-200">
                            <tr>
                                <th className="p-3 border-r w-10 text-center">#</th>
                                <th className="p-3 border-r">Institute Name</th>
                                <th className="p-3 border-r">Location</th>
                                <th className="p-3 border-r">Program / Course</th>
                                <th className="p-3 w-24 text-center">Status</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {displayList.map((item, index) => (
                                <tr key={index} className="hover:bg-gray-50">
                                    <td className="p-3 border-r text-center text-gray-500 font-medium">{index + 1}</td>
                                    <td className="p-3 border-r">
                                        <p className="font-bold text-gray-800">{item.name}</p>
                                        {item.rating && (
                                            <p className="text-[10px] text-yellow-600 flex items-center gap-1 mt-0.5">
                                                ⭐ {item.rating}/5
                                            </p>
                                        )}
                                    </td>
                                    <td className="p-3 border-r text-gray-700">
                                        <div className="flex items-center gap-1">
                                            <MapPin className="w-3 h-3 text-gray-400" />
                                            {item.city || '-'}, {item.state || '-'}
                                        </div>
                                    </td>
                                    <td className="p-3 border-r text-gray-700 font-medium">{item.course}</td>
                                    <td className="p-3 text-center">
                                        <span className="inline-block px-2 py-1 bg-green-100 text-green-700 text-[10px] font-bold rounded border border-green-200">
                                            ✓ APPLIED
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                ) : (
                    <div className="p-6 text-center text-gray-500 italic bg-gray-50">
                        No college details available for display.
                    </div>
                )}
            </div>
          </div>

          {/* 6. Payment Status Section */}
          <div className="mb-8 border border-gray-300 rounded-lg overflow-hidden relative z-10">
            <div className="bg-blue-900 text-white px-4 py-2 text-center text-sm font-bold uppercase">
                Fee Payment Status
            </div>
            <div className="grid grid-cols-4 text-xs md:text-sm bg-blue-50">
                <div className="p-3 border-r border-gray-300 text-center">
                    <div className="text-gray-500 text-[10px] uppercase mb-1">Status</div>
                    <div className="font-bold text-green-600 uppercase">{paymentStatus}</div>
                </div>
                <div className="p-3 border-r border-gray-300 text-center">
                    <div className="text-gray-500 text-[10px] uppercase mb-1">Transaction ID</div>
                    <div className="font-mono text-gray-800 break-all">{paymentId || "N/A"}</div>
                </div>
                <div className="p-3 border-r border-gray-300 text-center">
                    <div className="text-gray-500 text-[10px] uppercase mb-1">Amount</div>
                    <div className="font-bold text-gray-900">₹ {application.applicationFee}</div>
                </div>
                <div className="p-3 text-center">
                    <div className="text-gray-500 text-[10px] uppercase mb-1">Date</div>
                    <div className="font-medium text-gray-800 flex items-center justify-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {createdAt ? new Date(createdAt).toLocaleDateString() : '-'}
                    </div>
                </div>
            </div>
          </div>

          {/* 7. Footer & Declaration */}
          <div className="border-t-2 border-gray-300 pt-6 mt-8 relative z-10">
            <p className="text-[10px] text-gray-500 text-justify leading-snug mb-8">
              <strong>DECLARATION:</strong> I hereby declare that all the particulars given by me in this form are true to the best of my knowledge and belief. I understand that my admission is subject to verification of eligibility and original documents.
            </p>
            
            <div className="flex justify-between items-end">
              <div className="text-xs text-gray-400">
                <p>Printed from <strong>Vidyazon.com</strong></p>
                <p>Date: {new Date().toLocaleString()}</p>
              </div>
              <div className="text-center w-48">
                {documents?.signature && <img src={documents.signature} alt="Sign" className="h-10 mx-auto opacity-80 mb-1" />}
                <div className="border-t border-gray-400 pt-1 text-xs font-bold text-gray-700 uppercase">Signature of Candidate</div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

const DetailRow = ({ label, value }) => (
  <div className="flex border-b border-gray-200 last:border-0">
    <div className="w-1/3 bg-gray-50 p-2 font-semibold text-gray-600 border-r border-gray-200 flex items-center text-xs">{label}</div>
    <div className="w-2/3 p-2 text-gray-900 font-medium break-words text-sm">{value || '-'}</div>
  </div>
);

export default ApplicationConfirmation;